import { createSteamRuntime } from './106-steam-runtime.mjs';
import { steamMarkup } from './102-steam-template.mjs';
import { attachSteamInput } from './200-steam-input.mjs';
import { createSteamArcade } from './201-steam-arcade.mjs';
/** Own one isolated shadow surface, active-time clock and cancellable frame lease.
 * Host authorization and visibility are checked before input and every simulation step.
 * Physics always advances at 120Hz; frame pressure only reduces decorative work. */
export function createSteamPlayer(
  ctx,
  { host, seed, allowed, visible, onStart, onProgress, onCue = () => {} },
) {
  const surface = ctx.e('div', 'steam-player');
  host.append(surface);
  const root = surface.attachShadow({ mode: 'open' });
  const template = document.createElement('template');
  template.innerHTML = steamMarkup;
  root.append(template.content.cloneNode(true));
  const sheetUrls = [
    new URL('../styles/60-steam-host.css?no-inline', import.meta.url).href,
    new URL('../styles/59-steam-5.css?no-inline', import.meta.url).href,
    new URL('../styles/58-steam-4.css?no-inline', import.meta.url).href,
    new URL('../styles/57-steam-3.css?no-inline', import.meta.url).href,
    new URL('../styles/56-steam-2.css?no-inline', import.meta.url).href,
    new URL('../styles/55-steam-1.css?no-inline', import.meta.url).href,
  ];
  for (const url of sheetUrls) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    root.prepend(link);
  }
  let input = null;
  let disposed = false,
    frameId = 0,
    last = null;
  const controller = new AbortController();
  const r = createSteamRuntime(root, seed, () => wake());
  const clock = r.clock;
  const arcade = createSteamArcade(r);
  const { game, ui, canvas } = r;
  ui.scoreboard.append(root.querySelector('#clear-records'), ui.scoreboardStatus);
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  canvas.setAttribute(
    'aria-label',
    'Star Killer. Left and right aim; Space launches; N deploys neutron fuel; P pauses.',
  );
  const canPlay = () => !disposed && allowed() && visible();
  const listen = (node, type, fn, options = {}) =>
    node?.addEventListener(
      type,
      (event) => {
        if (!disposed) fn(event);
      },
      { ...options, signal: controller.signal },
    );
  function paint() {
    if (disposed) return;
    const saved = [game.shake, game.screenFlash, game.launchFlash, r.now];
    if (motion.matches) {
      game.shake = 0;
      game.screenFlash = 0;
      game.launchFlash = 0;
      r.now = () => 1000;
    }
    try {
      r.draw();
    } finally {
      [game.shake, game.screenFlash, game.launchFlash, r.now] = saved;
    }
    r.syncHud();
    arcade.paint();
    ui.pause.textContent = game.status === 'paused' ? 'Resume' : 'Pause';
    ui.pause.disabled = !game.modeRunStarted || game.status === 'gameover';
    const playing = active() && canPlay();
    host.dataset.playing = String(playing);
    root.querySelector('.steam-surface').dataset.playing = String(playing);
    root.querySelector('#run-utilities').hidden = playing || !game.modeRunStarted;
    root.querySelector('.layout-upgrades').hidden = playing || !game.modeRunStarted;
    root.querySelector('.expedition-hero').hidden = game.modeRunStarted;
    const chain = game.skillChain,
      label = root.querySelector('#chain-status');
    label.hidden = !chain.active;
    const chainText =
      chain.multiplier > 1
        ? `CHAIN ×${chain.multiplier} · ${Math.ceil(chain.multiplierRemainingSeconds)}s`
        : `Fire another special · ${Math.ceil(chain.windowRemainingSeconds)}s`;
    if (label.textContent !== chainText) label.textContent = chainText;
  }
  function pause() {
    if (disposed) return;
    if (game.status !== 'gameover' && game.modeRunStarted) game.status = 'paused';
    input?.cancel();
    game.aiming = false;
    game.neutronPointerArmed = false;
    cancelAnimationFrame(frameId);
    frameId = 0;
    last = null;
    clock.pause();
    paint();
  }
  r.pause = pause;
  function active() {
    return (
      game.status !== 'paused' &&
      game.status !== 'gameover' &&
      (game.modeRunStarted || game.volleyActive || game.launchQueue > 0)
    );
  }
  function frame(now) {
    frameId = 0;
    if (!canPlay()) {
      pause();
      return;
    }
    const previousScore = game.score;
    if (last !== null) clock.advance(now - last, active, r.update);
    if (game.score > previousScore)
      onCue('pickup', { volume: 0.4, channel: 'steam-reward', cooldown: 0.08 });
    last = now;
    paint();
    if (active()) frameId = requestAnimationFrame(frame);
    else {
      last = null;
      onProgress?.({ completed: game.status === 'gameover', score: game.score });
    }
  }
  function wake() {
    if (!canPlay()) return;
    paint();
    if (active() && !frameId) {
      last = null;
      frameId = requestAnimationFrame(frame);
    }
  }
  function launch(event) {
    event?.preventDefault();
    if (
      !canPlay() ||
      game.status === 'paused' ||
      ui.rulesModal.classList.contains('is-visible') ||
      game.scoreboardMode === 'view'
    )
      return;
    onStart?.();
    const wasActive = game.volleyActive;
    r.launchVolley();
    if (!wasActive && game.volleyActive) onCue('bounce', { volume: 0.25, rate: 1.1 });
    if (game.modeRunStarted) canvas.focus({ preventScroll: true });
    wake();
  }
  r.forceLaunch = launch;
  function rules() {
    pause();
    ui.rulesModal.classList.add('is-visible');
    ui.rulesModal.setAttribute('aria-hidden', 'false');
    ui.rulesClose.focus();
  }
  function closeRules() {
    ui.rulesModal.classList.remove('is-visible');
    ui.rulesModal.setAttribute('aria-hidden', 'true');
    ui.rulesOpen.focus();
  }
  listen(ui.primary, 'click', (e) => {
    if (game.scoreboardMode === 'view') {
      r.hideScoreboard();
      game.scoreboardMode = 'default';
      if (!game.modeRunStarted) {
        r.showOverlay(
          'Ready',
          r.currentModeConfig().overlayTitle,
          r.currentModeConfig().overlayCopy,
        );
      } else r.hideOverlay();
      ui.pause.focus();
      return;
    }
    r.handlePrimaryAction(e);
    wake();
  });
  listen(ui.pause, 'click', () => {
    if (!canPlay()) return;
    if (game.status === 'paused') {
      game.status = game.volleyActive ? 'playing' : 'ready';
      onStart?.();
      wake();
    } else pause();
  });
  listen(ui.restart, 'click', () => {
    pause();
    r.resetGame({ preserveMode: true });
    paint();
  });
  listen(ui.rulesOpen, 'click', rules);
  listen(ui.rulesClose, 'click', closeRules);
  listen(ui.highScoresOpen, 'click', r.openHighScores);
  listen(ui.rulesScoresOpen, 'click', () => {
    closeRules();
    r.openHighScores();
  });
  listen(ui.saveScore, 'click', r.saveCurrentScore);
  listen(root.querySelector('#clear-records'), 'click', r.clearRecords);
  for (const tab of ui.scoreboardTabs)
    listen(tab, 'click', () => {
      game.scoreboardRange = tab.dataset.boardRange;
      r.renderScoreboard();
    });
  for (const tab of ui.scoreboardModeTabs)
    listen(tab, 'click', () => {
      game.scoreboardBoardMode = tab.dataset.boardMode;
      r.renderScoreboard();
    });
  for (const button of ui.modeButtons)
    listen(button, 'click', () => {
      if (!game.modeRunStarted || game.status === 'gameover') {
        r.selectGameMode(button.dataset.gameMode);
        paint();
      }
    });
  for (const button of ui.upgradeButtons)
    listen(button, 'click', () => {
      if (canPlay() && game.status !== 'paused') {
        r.buyUpgrade(button.dataset.upgrade);
        wake();
      }
    });
  listen(ui.autoUpgrade, 'change', () => {
    game.autoUpgrade = r.currentModeConfig().forceAutoUpgrade || ui.autoUpgrade.checked;
    r.runAutoUpgrade();
    paint();
  });
  input = attachSteamInput(r, { listen, canPlay, paint, launch, wake });
  listen(root, 'pointerdown', () => r.recordSpeedIdleInteraction(), { passive: true });
  listen(root, 'keydown', (e) => {
    if (e.key === 'Escape') {
      closeRules();
      return;
    }
    if (e.target !== canvas) return;
    if (['ArrowLeft', 'ArrowRight', ' ', 'Enter', 'n', 'N', 'p', 'P'].includes(e.key))
      e.preventDefault();
    if (!canPlay()) return;
    r.recordSpeedIdleInteraction();
    if (e.key.toLowerCase() === 'p') {
      ui.pause.click();
      return;
    }
    if (game.status === 'paused') return;
    if (e.key === 'ArrowLeft') game.angle = r.clamp(game.angle + 2, 16, 164);
    if (e.key === 'ArrowRight') game.angle = r.clamp(game.angle - 2, 16, 164);
    if (e.key === ' ' || e.key === 'Enter') launch(e);
    if (e.key.toLowerCase() === 'n') r.spawnNeutronBombDeployment();
    wake();
  });
  listen(document, 'visibilitychange', () => {
    if (!visible()) pause();
  });
  // No page-level selectors, global debug state, service workers or original API calls.
  r.resetGame();
  paint();
  return {
    pause,
    cancel: pause,
    refresh() {
      if (
        !canPlay() &&
        (frameId ||
          game.aiming ||
          (game.modeRunStarted && game.status !== 'paused' && game.status !== 'gameover'))
      )
        pause();
    },
    dispose() {
      if (disposed) return;
      pause();
      disposed = true;
      input?.dispose();
      arcade.dispose();
      controller.abort();
      r.nebulaImage.onload = null;
      r.turretImage.onload = null;
      r.nebulaImage.src = '';
      r.turretImage.src = '';
      for (const cache of [
        r.ballSpriteCache,
        r.brickSpriteCache,
        r.planetSpriteCache,
        r.neutronFuelGaugeSpriteCache,
      ])
        cache.clear();
      surface.remove();
    },
  };
}
